import { Document } from '@contentful/rich-text-types'
import { css } from '@emotion/core'
import dayjs from 'dayjs'
import { graphql } from 'gatsby'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import React from 'react'

import Anchors from '../../components/Anchors'
import { AuthorAvatar } from '../../components/AuthorAvatar'
import Block from '../../components/Block'
import Body from '../../components/Body'
import LearnArticleCard from '../../components/content-management/product-learn/LearnArticleCard'
import RichText from '../../components/content-management/RichText'
import CTAMain from '../../components/ctas/CTAMain'
import Divider from '../../components/Divider'
import Footer from '../../components/Footer'
import { HubspotForm } from '../../components/HubspotForm'
import Layout from '../../components/Layout'
import { Link } from '../../components/Link'
import ReadingProgressBar from '../../components/ReadingProgressBar'
import { Schema } from '../../components/Schema'
import ScrollToTop from '../../components/ScrollToTop'
import SEO from '../../components/SEO'
import SocialSharing from '../../components/SocialSharing'
import { breakpoints } from '../../constants/breakPoints'
import { longCopyBreakPoint } from '../../constants/breakPoints'
import { isMobile } from '../../helpers/common'
import { enhanceContentObject, getAnchors, richTextRenderingOptions } from '../../helpers/contentful'
import { getCtaMainType, getCtaSidebar } from '../../helpers/cta'
import { useHsFormCallback } from '../../hooks/useHsFormCallback'
import { LearnArticleProps, learnBreakPoint } from '../../interfaces/learn'
import { LearnHeader } from '../../pages/product-learn'
import { anchorsFallbackStyle } from '../blog/BlogPost'
import { longCopyBlockStyle } from '../resources/GuidePage'

const LearnArticle: React.FunctionComponent<LearnArticleProps> = ({
  data: {
    article: {
      soon,
      slug,
      title,
      content,
      category,
      funnelStage,
      subcategories,
      author,
      createdAt,
      abstract: { abstract },
      heroImage,
    },
    topArticles,
  },
}) => {
  const [isFormLoaded, setIsFormLoaded] = React.useState(false)
  const json = React.useMemo(() => JSON.parse(content.raw) as Document, [content])
  const anchors = React.useMemo(() => getAnchors(json), [json])
  const articleContent = enhanceContentObject(content, funnelStage, category.slug)

  useHsFormCallback({
    onFormReadyCallback: () => setIsFormLoaded(true),
    hubspotFormIds: ['37a00ca3-1541-45b4-869c-51c71cc071f3'],
  })

  return (
    <Layout blackBG>
      <SEO title={title} description={abstract} imageSrc={`https:${heroImage.file.url}`} noindex={soon} />
      <Schema
        type={'LearningResource'}
        headline={title}
        url={`https://airfocus.com/product-learn/${slug}/`}
        dateCreated={createdAt}
        abstract={abstract}
        author={author.name}
      />
      <LearnHeader hideOnScroll />
      <Body>
        <ReadingProgressBar />
        <ScrollToTop />

        <div css={heroContainerStyle}>
          <p className={'strong-body-1 center white-text'}>{dayjs(createdAt).format('D MMM YYYY')}</p>
          <h1 className={'heading2 center'} css={titleStyle}>
            {title}
          </h1>
          <Link
            href={`/product-learn/category/${category.slug}/`}
            className={'center categories opacity-on-hover'}
            css={categoryStyle(category.categoryColor)}
          >
            {category.name.replace('<br />', '')}
          </Link>
          {subcategories && (
            <Block
              columns={subcategories.length}
              noBreakForTablet
              css={subcategoriesStyle}
              columnsTemplate={`repeat(${subcategories.length}, max-content)`}
              columnGap={0.5}
              rowGap={1}
            >
              {subcategories.map(({ name, slug }) => (
                <Link key={slug} className={'opacity-on-hover'} href={`/product-learn/subcategory/${slug}/`}>
                  {name}
                </Link>
              ))}
            </Block>
          )}
        </div>

        {/* AuthorAvatar is shown above article for tablet & mobile */}
        {isMobile(longCopyBreakPoint) && (
          <>
            <Divider size={4} isTop={false} color={'rgba(136, 146, 158, 0.4)'} notDecreased />
            <AuthorAvatar withDescription author={author} />
            <Divider size={4} color={'rgba(136, 146, 158, 0.4)'} notDecreased />
          </>
        )}

        <div
          css={css`
            position: relative;
          `}
        >
          {soon && (
            <div css={blurBackgroundStyle}>
              <div className={`center ${isFormLoaded && 'block-with-animation'}`}>
                <h3 className={'heading3'}>Subscribe to get notified when this article gets published</h3>
                <HubspotForm hubspotFormId={'37a00ca3-1541-45b4-869c-51c71cc071f3'} isCentered blackBG submitOnRight />
              </div>
            </div>
          )}
          <Block css={longCopyBlockStyle}>
            {anchors.length === 0 ? (
              <h3 className={'heading3 no-margin hide-on-small-medium'} css={anchorsFallbackStyle}>
                {title}
              </h3>
            ) : (
              <Anchors anchors={anchors} blackBG />
            )}
            <div>
              <RichText>
                {soon ? (
                  <React.Fragment>
                    <p className={'heading3'}>Guide On Running Effective sessions</p>
                    <p className={'paragraph'}>
                      Luckily for all the overworked project managers out there, there are plenty of brilliant tools
                      designed specifically to help.
                    </p>
                    <p className={'paragraph'}>
                      But before you dive headfirst into your next project, there are a few things you need to know.
                    </p>
                    <p className={'paragraph'}>
                      It's not quite as simple as closing your eyes, picking the first program that crosses your path,
                      and getting started. There are many different product roadmap tools out there, each with its own
                      particular strengths and weaknesses.
                    </p>
                    <p className={'paragraph'}>
                      Exploring the myriad options and finding the program that's right for you and your specific needs
                      can take time. It is almost a project in need of its own roadmap!
                    </p>
                    <p className={'paragraph'}>
                      But don't worry. We've already done the legwork, so you don't have to.{' '}
                    </p>
                  </React.Fragment>
                ) : (
                  !!content && renderRichText(articleContent, richTextRenderingOptions)
                )}
              </RichText>
              <Divider size={2} color={'rgba(136, 146, 158, 0.4)'} />
              <SocialSharing blackBG title={title} />
            </div>
            {!isMobile(longCopyBreakPoint) && (
              <div css={sidebarStyle}>
                <AuthorAvatar author={author} withDescription isInSidebar />
                <Divider size={6.5} color={'rgba(136, 146, 158, 0.4)'} />
                {getCtaSidebar(funnelStage, category.slug)}
              </div>
            )}
          </Block>
        </div>

        <Divider size={8} color={'rgba(136, 146, 158, 0.4)'} />

        <h3
          className={'heading3'}
          css={css`
            margin-bottom: 2rem;
          `}
        >
          Read also
        </h3>
        <Block
          columns={2}
          columnsTemplate={'2fr 1fr'}
          alignContent={'stretch'}
          css={readAlsoBlockStyle(learnBreakPoint)}
        >
          <Block columns={2} alignContent={'stretch'} noBreakForTablet>
            <LearnArticleCard
              title={topArticles.edges[0].node.title}
              description={topArticles.edges[0].node.abstract.abstract}
              author={topArticles.edges[0].node.author}
              categoryColor={topArticles.edges[0].node.category.categoryColor}
              isSoon={topArticles.edges[0].node.soon}
              articleUrl={topArticles.edges[0].node.slug}
              createdAt={topArticles.edges[0].node.createdAt}
            />
            <LearnArticleCard
              title={topArticles.edges[1].node.title}
              description={topArticles.edges[1].node.abstract.abstract}
              author={topArticles.edges[1].node.author}
              categoryColor={topArticles.edges[1].node.category.categoryColor}
              isSoon={topArticles.edges[1].node.soon}
              articleUrl={topArticles.edges[1].node.slug}
              createdAt={topArticles.edges[1].node.createdAt}
            />
          </Block>
          <Block rowGap={1} css={readAlsoSmallStyle(learnBreakPoint)}>
            {topArticles.edges.slice(2, 5).map(
              ({
                node: {
                  slug,
                  category: { categoryColor, categoryWhiteLogo },
                  author,
                  createdAt,
                  abstract: { abstract },
                  title,
                },
              }) => {
                return (
                  <LearnArticleCard
                    isSmall
                    key={slug}
                    isSoon={soon}
                    categoryColor={categoryColor}
                    author={author}
                    description={abstract}
                    title={title}
                    articleUrl={slug}
                    logoSrc={categoryWhiteLogo}
                    createdAt={createdAt}
                  />
                )
              }
            )}
          </Block>
        </Block>

        <CTAMain instantTourVersion type={getCtaMainType(category.slug)} />
      </Body>
      <Footer blackBG />
    </Layout>
  )
}

export default LearnArticle

export const query = graphql`
  query LearnArticlePage($id: String!) {
    article: contentfulLearnArticle(id: { eq: $id }) {
      soon
      slug
      title
      createdAt

      category {
        name
        slug
        categoryColor
      }

      subcategories {
        name
        slug
      }

      abstract {
        abstract
      }

      author {
        authorPhoto {
          gatsbyImageData(width: 80, quality: 90)
        }
        companyName
        jobTitle
        name
        slug
        description {
          description
        }
        authorPublications: learn_article {
          id
        }
      }

      heroImage {
        file {
          url
        }
      }

      funnelStage
      content {
        raw
        references {
          ... on ContentfulAsset {
            # contentful_id and __typename are is required to resolve the references
            title
            contentful_id
            __typename
            gatsbyImageData(width: 800, quality: 90)
          }
          ... on ContentfulCrossSellingEntry {
            id
            type
            ebookResource {
              slug
            }
            contentful_id
            __typename
            title
          }
          ... on Node {
            ... on ContentfulImageWithLink {
              id
              contentful_id
              __typename
              link
              image {
                title
                gatsbyImageData(width: 800, quality: 90)
              }
              description {
                description
              }
            }
          }
        }
      }
    }

    topArticles: allContentfulLearnArticle(
      sort: { createdAt: DESC }
      limit: 5
      filter: { id: { ne: $id }, soon: { eq: false } }
    ) {
      edges {
        node {
          soon
          slug
          title
          createdAt
          popular
          abstract {
            abstract
          }
          author {
            name
            slug
            authorPhoto {
              gatsbyImageData(width: 48, quality: 90)
            }
          }
          category {
            categoryColor
            categoryWhiteLogo {
              title
              gatsbyImageData(height: 32, quality: 90)
            }
          }
        }
      }
    }
  }
`

const subcategoriesStyle = css`
  color: #000;
  font-size: 0.75rem;
  line-height: 1rem;
  margin: 0 auto 1rem;
  width: max-content;

  a {
    color: #0a1c33;
    display: inline-block;
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    background-color: #efeff0;
    margin: 0 auto;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    margin: 0 auto !important;
  }
`

// export for <BlogPost />
export const sidebarStyle = css`
  height: 100%;
`

const heroContainerStyle = css`
  padding: 4.5rem 0 3rem;

  .strong-body-1 {
    margin-bottom: 0.5rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 3rem 0;

    .heading2 {
      text-align: center !important;
    }
  }
`

const blurBackgroundStyle = css`
  position: absolute;
  top: -1rem;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  background: linear-gradient(rgba(7, 19, 34, 0.69) 0%, #071322 100%);
  backdrop-filter: blur(15px);

  & > div {
    margin-top: 10rem;
    display: none;
  }

  @keyframes fadeInUp {
    0% {
      transform: translate3d(0, 100px, 0);
      opacity: 0;
    }
    100% {
      transform: translate3d(0, 0, 0);
      opacity: 1;
    }
  }

  .block-with-animation {
    display: block;
    animation-name: fadeInUp;
    animation-duration: 0.5s;
    animation-iteration-count: 1;
    animation-direction: normal;
    animation-timing-function: ease-out;
    animation-fill-mode: forwards;
  }

  .hs-dependent-field {
    text-align: left;
  }
`

const categoryStyle = (categoryColor: string) => css`
  color: ${categoryColor} !important;
  margin-bottom: 1rem;
  display: block;
`

const titleStyle = css`
  margin-bottom: 1rem !important;
`

const readAlsoBlockStyle = (breakpoint: number) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr;
  }
`

const readAlsoSmallStyle = (breakpoint: number) => css`
  @media screen and (min-width: ${breakpoints.small + 1}px) and (max-width: ${breakpoint}px) {
    grid-template-columns: 1fr 1fr;
  }
`
